'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  onOrderNow: () => void;
  onViewGallery: () => void;
}

const HeroSection = ({ onOrderNow, onViewGallery }: HeroSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const heroProducts = [
  {
    image: "/assets/images/mango-pickle-1766907651361.jpg",
    alt: 'Traditional Indian mango pickle in glass jar with golden oil and red spices on wooden table',
    title: 'Authentic Pickles'
  },
  {
    image: "/assets/images/deshi-ghee-1766909394511.jpg",
    alt: 'Golden deshi ghee in traditional brass container with wooden spoon on rustic background',
    title: 'Pure Deshi Ghee'
  },
  {
    image: "/assets/images/seasonal-murraba-1766909618317.jpg",
    alt: 'Colorful seasonal murrabas in decorative glass jars with fresh fruits on traditional cloth',
    title: 'Seasonal Murrabas'
  }];


  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroProducts.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isHydrated, heroProducts.length]);

  const handleSlideChange = (index: number) => {
    if (!isHydrated) return;
    setCurrentSlide(index);
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-background via-card to-muted">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,165,116,0.3),transparent_50%)]" />
      </div>

      <div className="relative mx-auto max-w-[1200px] px-6 py-16 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <div className="flex flex-col justify-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 w-fit">
              <Icon name="SparklesIcon" size={20} className="text-primary" />
              <span className="font-cta text-sm font-bold text-primary">
                Trusted by 5000+ Families
              </span>
            </div>

            <h1 className="font-heading mb-6 text-4xl font-bold leading-tight text-foreground lg:text-5xl xl:text-6xl">
              Authentic Indian Pickles,
              <br />
              <span className="text-primary">Murrabas & Deshi Ghee</span>
            </h1>

            <p className="mb-8 text-lg text-muted-foreground lg:text-xl">
              Authentic Indian pickles, murrabas & deshi ghee made exactly like
              your grandmother's recipes - zero preservatives, pure tradition.
            </p>

            <div className="flex flex-col gap-4 sm:flex-row">
              <button
                onClick={onOrderNow}
                className="group flex items-center justify-center gap-2 rounded-lg bg-primary px-8 py-4 font-cta text-base font-bold text-primary-foreground shadow-cta transition-all duration-250 ease-out hover:shadow-lg">

                Order Fresh Today
                <Icon
                  name="ShoppingBagIcon"
                  size={20}
                  className="transition-transform duration-250 ease-out group-hover:scale-110" />

              </button>

              <button
                onClick={onViewGallery}
                className="flex items-center justify-center gap-2 rounded-lg border-2 border-primary bg-background px-8 py-4 font-cta text-base font-bold text-primary transition-colors duration-250 ease-out hover:bg-primary/10">

                View Product Gallery
                <Icon name="PhotoIcon" size={20} />
              </button>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <Icon name="ShieldCheckIcon" size={24} className="text-success" />
                <span className="text-sm font-medium text-foreground">
                  FSSAI Certified
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="CheckBadgeIcon" size={24} className="text-success" />
                <span className="text-sm font-medium text-foreground">
                  100% Organic
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="HeartIcon" size={24} className="text-error" />
                <span className="text-sm font-medium text-foreground">
                  Zero Preservatives
                </span>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="relative aspect-square overflow-hidden rounded-2xl bg-muted shadow-cta">
              {isHydrated ?
              <AppImage
                src={heroProducts[currentSlide].image}
                alt={heroProducts[currentSlide].alt}
                className="h-full w-full object-cover transition-transform duration-500 ease-out hover:scale-105" /> :


              <AppImage
                src={heroProducts[0].image}
                alt={heroProducts[0].alt}
                className="h-full w-full object-cover" />

              }

              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-foreground/80 to-transparent p-6">
                <h3 className="font-heading text-2xl font-bold text-background">
                  {isHydrated ?
                  heroProducts[currentSlide].title :
                  heroProducts[0].title}
                </h3>
              </div>
            </div>

            <div className="mt-6 flex justify-center gap-3">
              {heroProducts.map((_, index) =>
              <button
                key={index}
                onClick={() => handleSlideChange(index)}
                className={`h-2 rounded-full transition-all duration-250 ease-out ${
                (isHydrated ? currentSlide : 0) === index ?
                'w-8 bg-primary' : 'w-2 bg-border hover:bg-primary/50'}`
                }
                aria-label={`Go to slide ${index + 1}`} />

              )}
            </div>

            <div className="absolute -right-4 -top-4 animate-bounce">
              <div className="rounded-full bg-warning p-4 shadow-cta">
                <Icon name="SparklesIcon" size={32} className="text-warning-foreground" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;